import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import axios from "axios";
import { decryptData } from "../../../../crypto";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px 8px",
    borderBottom: "1px solid #e0e0e0",
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.1rem",
    color: "#1a1a2e",
    fontFamily: "Outfit, sans-serif",
  },
  closeButton: {
    color: "#666",
    padding: 6,
  },
  dialogContent: {
    padding: "24px",
    minWidth: 340,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
  },
  subtitle: {
    color: "#555",
    fontSize: "0.875rem",
    textAlign: "center",
    fontFamily: "Outfit, sans-serif",
    marginBottom: 8,
  },
  otpInput: {
    width: "100%",
    "& input": {
      textAlign: "center",
      fontSize: "1.5rem",
      letterSpacing: "0.5rem",
      fontWeight: 700,
    },
  },
  resendRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 4,
  },
  resendText: {
    fontSize: "0.8rem",
    color: "#888",
    fontFamily: "Outfit, sans-serif",
  },
  resendBtn: {
    fontSize: "0.8rem",
    color: "#1565c0",
    fontWeight: 600,
    textTransform: "none",
    padding: "0 4px",
    minWidth: "unset",
    fontFamily: "Outfit, sans-serif",
    "&:disabled": {
      color: "#aaa",
    },
  },
  dialogActions: {
    padding: "12px 24px 20px",
    justifyContent: "center",
  },
  submitBtn: {
    backgroundColor: "#1565c0",
    color: "#fff",
    fontWeight: 600,
    fontFamily: "Outfit, sans-serif",
    padding: "10px 40px",
    borderRadius: 8,
    textTransform: "none",
    fontSize: "0.95rem",
    "&:hover": {
      backgroundColor: "#0d47a1",
    },
    "&:disabled": {
      backgroundColor: "#90caf9",
      color: "#fff",
    },
  },
  timerText: {
    fontSize: "0.8rem",
    color: "#888",
    fontFamily: "Outfit, sans-serif",
  },
}));

/**
 * OtpPopup
 *
 * Props:
 *   open          {boolean}  — controls dialog visibility
 *   onClose       {func}     — called ONLY when X button is clicked
 *   onVerified    {func}     — called after successful OTP verification
 *   sendOtpApi    {string}   — API URL to send/resend OTP  (POST)
 *   verifyOtpApi  {string}   — API URL to verify OTP       (POST)
 *   payload       {object}   — extra body fields sent with sendOtp (optional)
 *   otpFieldKey   {string}   — key name for otp in verify body (default: "otp")
 */
function OtpPopup({
  open,
  onClose,
  onVerified,
  sendOtpApi,
  verifyOtpApi,
  payload = {},
  resendPayload,     // separate body for resend (e.g. { phone } only)
  otpFieldKey = "otp",
  autoSend = true,
}) {
  const classes = useStyles();
  const decryptedToken = decryptData(sessionStorage.getItem("token"));

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0); // seconds remaining before resend allowed

  // Send OTP when popup opens (only if autoSend is enabled)
  useEffect(() => {
    if (open) {
      setOtp("");
      if (autoSend) sendOtp();
      else setCountdown(30); // still start the resend countdown
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const sendOtp = async () => {
    if (!sendOtpApi) return;
    setResendLoading(true);
    try {
      await axios.post(
        sendOtpApi,
        resendPayload !== undefined ? resendPayload : { ...payload },
        { headers: { Authorization: `Bearer ${decryptedToken}` } }
      );
      toast.success("OTP sent successfully");
      setCountdown(30); // allow resend after 30 seconds
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Failed to send OTP. Please try again.";
      toast.error(msg);
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!otp || otp.trim().length === 0) {
      toast.error("Please enter the OTP");
      return;
    }
    setLoading(true);
    try {
      if (!verifyOtpApi) {
        if (onVerified) await onVerified(null, otp);
        return;
      }

      const response = await axios.post(
        verifyOtpApi,
        { ...payload, [otpFieldKey]: otp },
        { headers: { Authorization: `Bearer ${decryptedToken}` } }
      );
      toast.success(response?.data?.message || "OTP verified successfully");
      setOtp("");
      if (onVerified) await onVerified(response.data, otp);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Verification failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Block closing by backdrop/ESC — only X button closes
  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      PaperProps={{ style: { borderRadius: 12, minWidth: 360 } }}
    >
      {/* Title row */}
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography className={classes.titleText}>OTP Verification</Typography>
        <IconButton
          className={classes.closeButton}
          onClick={() => { setOtp(""); if (onClose) onClose(); }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.subtitle}>
          An OTP has been sent. Please enter it below to verify.
        </Typography>

        <TextField
          className={classes.otpInput}
          variant="outlined"
          placeholder="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
          inputProps={{ maxLength: 8 }}
          disabled={loading}
          onKeyPress={(e) => { if (e.key === "Enter") handleVerify(); }}
          autoFocus
        />

        {/* Resend row */}
        <div className={classes.resendRow}>
          {countdown > 0 ? (
            <Typography className={classes.timerText}>
              Resend OTP in <strong>{countdown}s</strong>
            </Typography>
          ) : (
            <>
              <Typography className={classes.resendText}>
                Didn't receive the OTP?
              </Typography>
              <Button
                className={classes.resendBtn}
                onClick={sendOtp}
                disabled={resendLoading || loading}
                disableRipple
              >
                {resendLoading ? "Sending..." : "Resend OTP"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>

      {/* Submit */}
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.submitBtn}
          onClick={handleVerify}
          disabled={loading || otp.trim().length === 0}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "#fff" }} />
          ) : (
            "Verify & Submit"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OtpPopup;
